import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Result } from 'antd';
import { SEOHead } from '../components/SEO/SEOHead';
import { useI18n } from '../i18n/I18nContext';
import { redirectsApi, getOrCreateVisitorId } from '../api/redirects';

const SLUG_REGEX = /^[a-zA-Z0-9_-]{1,64}$/;

function isSafeRedirectUrl(url: string): boolean {
  const t = url.trim();
  if (!t) return false;
  try {
    const u = new URL(t);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Page publique /r/:slug qui résout le slug, enregistre le clic, puis redirige vers l'URL configurée.
 */
const RedirectResolve: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setError(t.app.redirectResolve.missingSlug);
      return;
    }
    if (!SLUG_REGEX.test(slug)) {
      setError(t.app.redirectResolve.invalidLink);
      return;
    }
    let cancelled = false;
    const visitorId = getOrCreateVisitorId();
    redirectsApi
      .resolve(slug)
      .then(({ url }) => {
        if (cancelled) return;
        if (!isSafeRedirectUrl(url)) {
          setError(t.app.redirectResolve.invalidLink);
          return;
        }
        redirectsApi.track(slug, visitorId);
        window.location.replace(url);
      })
      .catch(() => {
        if (!cancelled) {
          setError(t.app.redirectResolve.notFound);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, t]);

  if (error) {
    return (
      <>
        <SEOHead title={t.app.redirectResolve.notFoundTitle} noindex nofollow />
        <div style={{ padding: '3rem', maxWidth: 500, margin: '0 auto' }}>
          <Result status="404" title={t.app.redirectResolve.notFoundTitle} subTitle={error} />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead title={t.app.redirectResolve.redirectTitle} noindex nofollow />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip={t.app.redirectResolve.redirecting} />
      </div>
    </>
  );
};

export default RedirectResolve;
